import React, { useState, useEffect } from 'react'
import { Box, Typography, Button, Grid2 } from '@mui/material'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import { utils as xlsxUtils, writeFile as xlsxWriteFile } from 'xlsx'
import DownloadIcon from '@mui/icons-material/Download'
import ExcelJS from 'exceljs'

import PageHeader from '../components/common/PageHeader'
import api from '../services/authService'
import projectService, { Project } from '../services/projectService'
import type { CheckInOut, CheckInOutRecord } from '../types/checkinout'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { th } from 'date-fns/locale'
import FilterGrid2 from '../components/common/Filters/FilterGrid2'
import SingleSelectFilter from '../components/common/Filters/SingleSelectFilter'
import CustomAutocomplete from '../components/common/Filters/CustomAutocomplete'
import RangeDateFilter from '../components/common/Filters/RangeDateFilter'
import { convertThaiDateToISO } from '../utils/dateUtils'
import dayjs from 'dayjs'
import { CheckInOutStatus } from '../types/checkinout'
import {
  handleExport,
  handleExport2,
  handleExportExcelJS,
  handleAllDayExportExcelJS,
} from '../utils/exportUtils'

interface CheckInOutResponse {
  data: CheckInOut[]
  meta: {
    total: number
    page: number
    perPage: number
    totalPages: number
  }
}

// ประเภทข้อมูลสำหรับผู้ใช้
interface User {
  _id: string
  username: string
  name: string
  employeeCode?: string
  phoneNumber?: string
  position?: string
  isEnable: boolean
  objectId?: string
}

interface UsersResponse {
  data: User[]
  meta: {
    total: number
    page: number
    perPage: number
    totalPages: number
  }
}

const Documents: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [checkInOutRecords, setCheckInOutRecords] = useState<
    CheckInOutRecord[]
  >([])
  const [selectedProject, setSelectedProject] = useState('')
  const [startDate, setStartDate] = useState(
    dayjs().startOf('month').format('YYYY-MM-DD'),
  )
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const rowsPerPage = 10
  const [projects, setProjects] = useState<Project[]>([])
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)

  // สถานะสำหรับ Modal
  const [exportLoading, setExportLoading] = useState(false)

  useEffect(() => {
    fetchProjects()
    fetchCheckInOutData()
  }, [page])

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true)
      const { projects: projectList } = await projectService.getProjects({
        page: 1,
        perPage: 100,
      })
      setProjects(projectList)
    } catch (error) {
      console.error('Error fetching projects:', error)
    } finally {
      setLoadingProjects(false)
    }
  }

  const fetchUsers = async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 2) {
      setUsers([])
      return
    }

    try {
      setLoadingUsers(true)
      const response = await api.get<UsersResponse>('/users', {
        params: {
          search: searchQuery,
          page: 1,
          perPage: 10,
        },
      })

      setUsers(response.data.data)
    } catch (error) {
      console.error('Error fetching users:', error)
      setUsers([])
    } finally {
      setLoadingUsers(false)
    }
  }

  const fetchCheckInOutData = async () => {
    try {
      setLoading(true)
      const response = await api.get<CheckInOutRecord[]>(
        '/project-checkins/txns',
        {
          params: {
            page,
            perPage: rowsPerPage,
            projectId: selectedProject || undefined,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            username: selectedUser?.username || undefined,
          },
        },
      )

      setCheckInOutRecords(response.data)
      // setTotalPages(response.data.meta.totalPages);
    } catch (error) {
      console.error('Error fetching check-in/out data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleProjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedProject(event.target.value)
  }

  const handleStartDateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setStartDate(event.target.value)
  }

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value)
  }

  // ฟังก์ชันจัดการเปลี่ยนแปลงวันที่เริ่มต้น
  const handleRangeStartDateChange = (dateString: string) => {
    // แปลงรูปแบบวันที่จาก dd/mm/yyyy เป็น yyyy-mm-dd สำหรับ API
    setStartDate(convertThaiDateToISO(dateString))
  }

  // ฟังก์ชันจัดการเปลี่ยนแปลงวันที่สิ้นสุด
  const handleRangeEndDateChange = (dateString: string) => {
    // แปลงรูปแบบวันที่จาก dd/mm/yyyy เป็น yyyy-mm-dd สำหรับ API
    setEndDate(convertThaiDateToISO(dateString))
  }

  const handleUserInputChange = (
    _event: React.SyntheticEvent,
    value: string,
  ) => {
    setSearchTerm(value)
    fetchUsers(value)
  }

  const handleUserChange = (
    _event: React.SyntheticEvent,
    newValue: User | null,
  ) => {
    setSelectedUser(newValue)
  }

  const handleClearFilters = () => {
    setSelectedProject('')
    setStartDate('')
    setEndDate('')
    setSearchTerm('')
    setSelectedUser(null)
  }

  const handleExportExcelJSClick = async () => {
    try {
      setExportLoading(true)
      const response = await api.get<CheckInOutRecord[]>('/project-checkins/txns', {
        params: {
          projectId: selectedProject || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          username: selectedUser?.username || undefined,
        },
      })
      await handleExportExcelJS(response.data)
    } catch (error) {
      console.error('Error exporting data with ExcelJS:', error)
    } finally {
      setExportLoading(false)
    }
  }

  const handleAllDayExportExcelJSClick = async () => {
    try {
      setExportLoading(true)
      const response = await api.get<CheckInOutRecord[]>('/project-checkins/txns', {
        params: {
          projectId: selectedProject || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          username: selectedUser?.username || undefined,
        },
      })
      await handleAllDayExportExcelJS(response.data, startDate, endDate)
    } catch (error) {
      console.error('Error exporting data with ExcelJS:', error)
    } finally {
      setExportLoading(false)
    }
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: '100%',
      }}
    >
      <PageHeader title="ออกเอกสาร" />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 2, sm: 3 },
          mb: 3,
          gap: 1,
          pl: 2,
          borderRadius: 2,
          border: '1px solid #e0e0e0',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          ตั้งค่าการออกเอกสาร
        </Typography>

        <FilterGrid2>
          <Grid2 size={{ xs: 12, sm: 2 }}>
            <SingleSelectFilter
              filter={{
                label: 'โครงการ',
                value: 'project',
                options: projects.map((project) => ({
                  label: project.name,
                  value: project.objectId,
                })),
              }}
              searchable={true}
              value={selectedProject}
              onChange={handleProjectChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 2 }}>
            <CustomAutocomplete
              label="พนักงาน"
              placeholder="ค้นหาพนักงาน"
              options={users}
              value={selectedUser}
              onChange={handleUserChange}
              onInputChange={handleUserInputChange}
              loading={loadingUsers}
              noOptionsText="ไม่พบข้อมูลพนักงาน"
              loadingText="กำลังค้นหา..."
              getOptionLabel={(option) =>
                `${option.name}${
                  option.employeeCode ? ` (${option.employeeCode})` : ''
                }`
              }
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 2 }}>
            <SingleSelectFilter
              filter={{
                label: 'สถานะการใช้งาน',
                value: 'status',
                options: [
                  { label: 'ใช้งาน', value: 'active' },
                  { label: 'ไม่ใช้งาน', value: 'inactive' },
                ],
              }}
              value={''}
              onChange={(value) => {}}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>
            <RangeDateFilter
              onStartDateChange={handleRangeStartDateChange}
              onEndDateChange={handleRangeEndDateChange}
            />
          </Grid2>

          <Grid2>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              disabled={loading}
            >
              ล้างตัวกรอง
            </Button>
          </Grid2>
          <Grid2>
            <Button
              startIcon={<DownloadIcon />}
              variant="contained"
              onClick={handleAllDayExportExcelJSClick}
              disabled={exportLoading}
            >
              ดาวน์โหลด Excel
            </Button>
          </Grid2>
        </FilterGrid2>
      </Box>
    </Box>
  )
}

export default Documents
